import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Store,
  ShoppingCart,
  IndianRupee,
  Wallet,
  TrendingUp,
  Package,
  TrendingDown,
  Activity,
  Star,
  Box,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { API_URL } from '@/lib/api';

/* ─── Metric Card ─────────────────────────────────────── */
const cardConfig = {
  purple: {
    iconBg: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    iconColor: '#7c3aed',
  },
  green: {
    iconBg: 'linear-gradient(135deg, #059669, #34d399)',
    iconColor: '#059669',
  },
  amber: {
    iconBg: 'linear-gradient(135deg, #d97706, #fbbf24)',
    iconColor: '#d97706',
  },
  rose: {
    iconBg: 'linear-gradient(135deg, #e11d48, #fb7185)',
    iconColor: '#e11d48',
  },
};

const MetricCard = ({ title, value, icon: Icon, color, trend, delay = 0 }) => {
  const cfg = cardConfig[color] || cardConfig.purple;
  return (
    <div
      className="bg-white rounded-xl p-3.5 border border-slate-100 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 cursor-default animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`metric-${title.toLowerCase().replace(' ', '-')}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${cfg.iconColor}10` }}
        >
          <Icon className="w-5 h-5" style={{ color: cfg.iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
            {title}
          </p>
          <div className="flex items-baseline gap-2">
            <p className="text-lg font-heading font-bold text-slate-900 leading-tight">
              {value}
            </p>
            {trend && (
              <span className={`text-[10px] font-bold ${trend.positive ? 'text-emerald-600' : 'text-rose-500'}`}>
                {trend.positive ? '+' : ''}{trend.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Tooltip ─────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur p-2.5 rounded-lg shadow-xl border border-slate-100 text-xs">
        <p className="font-semibold text-slate-800 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} className="text-slate-600 flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full inline-block"
              style={{ background: entry.color }}
            />
            {entry.name === 'revenue'
              ? `Revenue: ₹${entry.value.toLocaleString()}`
              : `Orders: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

/* ─── Loading skeleton ────────────────────────────────── */
const Skeleton = ({ className = '' }) => (
  <div className={`shimmer rounded-lg ${className}`} />
);

/* ─── Dashboard ───────────────────────────────────────── */
const Dashboard = () => {
  const [stats, setStats] = useState({
    total_stores: 0,
    total_products: 0,
    total_orders: 0,
    revenue: 0,
    cod_outstanding: 0,
    pending_orders: 0,
    orders_today: 0,
    revenue_today: 0,
    avg_order_value: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topStores, setTopStores] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API_URL}/dashboard`);
      const d = res.data;
      setStats({
        total_stores: d.totalStores || 0,
        total_products: d.totalProducts || 0,
        total_orders: d.totalOrders || 0,
        revenue: d.totalRevenue || 0,
        cod_outstanding: d.codOutstanding || 0,
        pending_orders: d.pendingOrders ?? 0,
        orders_today: d.ordersToday ?? 0,
        revenue_today: d.revenueToday ?? 0,
        avg_order_value: d.avgOrderValue || 0,
      });
      setChartData(
        (d.ordersPerDay || []).slice(-7).map(x => ({
          date: new Date(x.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          orders: x.count,
          revenue: x.total || 0,
        }))
      );
      setTopProducts(
        (d.topProducts || []).slice(0, 10).map(p => ({
          product_name: p.name,
          total_quantity: p.quantity,
        }))
      );
      setTopStores(d.topStores || []);
      setRecentOrders(d.recentOrders || []);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-9 gap-4">
          {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="lg:col-span-2 h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard title="Revenue"          value={`₹${stats.revenue.toLocaleString()}`}       icon={IndianRupee}  color="green"  delay={0} />
        <MetricCard title="COD O/S"          value={`₹${stats.cod_outstanding.toLocaleString()}`} icon={Wallet}     color="rose"   delay={40} />
        <MetricCard title="Total Orders"     value={stats.total_orders.toLocaleString()}        icon={ShoppingCart} color="amber"  delay={80} />
        <MetricCard title="Total Stores"     value={stats.total_stores.toLocaleString()}       icon={Store}        color="purple" delay={120} />
        <MetricCard title="Avg. Order"       value={`₹${Math.round(stats.avg_order_value).toLocaleString()}`} icon={Activity} color="amber" delay={160} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Area chart */}
        <Card
          className="lg:col-span-2 rounded-xl border-slate-100 shadow-sm animate-fade-up bg-white"
          style={{ animationDelay: '100ms' }}
          data-testid="orders-chart"
        >
          <CardHeader className="pb-0 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-800">
                  Orders &amp; Revenue
                </CardTitle>
                <p className="text-[10px] text-slate-400">Last 7 days</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  Orders
                </span>
                <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  Revenue
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#7c3aed" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#059669" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9, fill: '#94a3b8' }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 9, fill: '#94a3b8' }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    yAxisId="right" orientation="right"
                    tick={{ fontSize: 9, fill: '#94a3b8' }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => `₹${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    yAxisId="left" type="monotone" dataKey="orders"
                    stroke="#7c3aed" strokeWidth={2}
                    fill="url(#gradOrders)" name="orders"
                    dot={false}
                    activeDot={{ r: 4, fill: '#7c3aed', strokeWidth: 0 }}
                  />
                  <Area
                    yAxisId="right" type="monotone" dataKey="revenue"
                    stroke="#059669" strokeWidth={2}
                    fill="url(#gradRevenue)" name="revenue"
                    dot={false}
                    activeDot={{ r: 4, fill: '#059669', strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top products */}
        <Card
          className="rounded-xl border-slate-100 shadow-sm animate-fade-up bg-white"
          style={{ animationDelay: '180ms' }}
          data-testid="top-products"
        >
          <CardHeader className="pb-0 pt-4">
            <CardTitle className="text-sm font-bold text-slate-800">
              Top Selling Products
            </CardTitle>
            <p className="text-[10px] text-slate-400">By quantity sold</p>
          </CardHeader>
          <CardContent className="pt-4">
            {topProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-300">
                <Package className="w-8 h-8 mb-2" />
                <p className="text-xs text-slate-400">No data</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topProducts}
                    layout="vertical"
                    margin={{ top: 0, right: 5, left: -25, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%"   stopColor="#7c3aed" />
                        <stop offset="100%" stopColor="#a855f7" />
                      </linearGradient>
                    </defs>
                    <XAxis
                      type="number" hide
                    />
                    <YAxis
                      dataKey="product_name" type="category"
                      tick={{ fontSize: 9, fill: '#64748b' }}
                      axisLine={false} tickLine={false} width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="total_quantity"
                      fill="url(#barGrad)"
                      radius={[0, 4, 4, 0]}
                      barSize={12}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Stores */}
        <Card className="rounded-xl border-slate-100 shadow-sm bg-white overflow-hidden">
          <CardHeader className="pb-3 pt-4 border-b border-slate-50">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <CardTitle className="text-sm font-bold text-slate-800">Top Performing Stores</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-50">
              {topStores.map((store, i) => (
                <div key={i} className="flex items-center justify-between p-3.5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                      #{i + 1}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">{store.name}</p>
                      <p className="text-[10px] text-slate-400">{store.orders} orders</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-emerald-600">₹{store.revenue.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="rounded-xl border-slate-100 shadow-sm bg-white overflow-hidden">
          <CardHeader className="pb-3 pt-4 border-b border-slate-50">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-500" />
              <CardTitle className="text-sm font-bold text-slate-800">Recent Activity</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-50">
              {recentOrders.map((order, i) => (
                <div key={i} className="flex items-center justify-between p-3.5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      order.status === 'DELIVERED' ? 'bg-emerald-500' : 
                      order.status === 'PENDING' ? 'bg-amber-500' : 'bg-slate-300'
                    }`} />
                    <div>
                      <p className="text-xs font-bold text-slate-800">{order.store}</p>
                      <p className="text-[10px] text-slate-400">{new Date(order.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-800">₹{order.amount.toLocaleString()}</p>
                    <p className="text-[10px] font-medium text-slate-400">{order.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
